import { KeyboardEvent, useId } from 'react';

import { Input } from '@/components/core/Input/Input';
import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '@/services/utils';

interface DamTagNameFieldProps {
    value: string;
    onChange: (value: string) => void;
    /** Enter; e.g. creates or renames the tag. */
    onSubmit: () => void;
    /** Escape; e.g. leaves create or rename mode. */
    onCancel?: () => void;
    error?: string;
    disabled?: boolean;
    /** Accessible name; defaults to "Tag name". */
    label?: string;
    autoFocus?: boolean;
    className?: string;
}

/**
 * Tag name input of the tags panel and the bulk tag dialog ("Type tag name ...",
 * wireframe `I7170:103385;6476:92549;7169:81965`); also used for the inline rename
 * (D-038). Shows a validation message such as "This tag already exists." below.
 */
function DamTagNameField({ value, onChange, onSubmit, onCancel, error, disabled, label, autoFocus, className }: DamTagNameFieldProps) {
    const { t } = useTranslation();
    const errorId = useId();

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            onSubmit();
        } else if (event.key === 'Escape' && onCancel) {
            event.preventDefault();
            event.stopPropagation();
            onCancel();
        }
    };

    return (
        <div className={cn('tw-flex tw-min-w-0 tw-flex-col tw-gap-1', className)}>
            <Input
                type="text"
                value={value}
                disabled={disabled}
                autoFocus={autoFocus}
                placeholder={t('DAM.TAGS.NAME_PLACEHOLDER')}
                aria-label={label ?? t('DAM.TAGS.NAME')}
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? errorId : undefined}
                onChange={(event) => onChange(event.target.value)}
                onKeyDown={handleKeyDown}
            />
            {error && (
                <span id={errorId} className="tw-text-t-sm tw-text-error lg:tw-text-t-base">{error}</span>
            )}
        </div>
    );
}

export { DamTagNameField };
export type { DamTagNameFieldProps };
